import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Nav, Tab } from "react-bootstrap";
import { handleError, handleSuccess } from "../CommonJquery/CommonJquery";
import MasterLayout from "../masterLayout/MasterLayout";
import {
  create_pinterest_board,
  create_pinterest_pin,
  delete_pinterest_board,
  delete_pinterest_pin,
  get_pinterest_account,
  get_pinterest_auth_url,
  get_pinterest_board_sections,
  get_pinterest_boards,
  get_pinterest_insights,
  get_pinterest_pin_insights,
  get_pinterest_pins,
  get_pinterest_top_pins,
  pinterest_oauth_callback,
  save_pinterest_account,
  server_post_data,
  update_pinterest_pin,
  upload_pinterest_video,
  upload_public_media,
} from "../ServiceConnection/serviceconnection";
import Loader from "./Loader";
import "../components/CSS/gmb-premium.css";
import "../components/CSS/facebook-premium.css";
import "../components/CSS/pinterest-premium.css";

/* ---------------------------------------------------------------------------
   Static config
--------------------------------------------------------------------------- */

const SOCIAL_PROFILES = [
  { key: "google", label: "Google Business", icon: "mdi:google", ready: false },
  { key: "facebook", label: "Facebook", icon: "mdi:facebook", ready: false },
  { key: "instagram", label: "Instagram", icon: "mdi:instagram", ready: false },
  { key: "pinterest", label: "Pinterest", icon: "mdi:pinterest", ready: true },
  { key: "twitter", label: "Twitter", icon: "ri:twitter-x-fill", ready: false },
  { key: "linkedin", label: "LinkedIn", icon: "mdi:linkedin", ready: false },
];

const CHANNEL_TABS = [
  { key: "pinterest", label: "Pinterest", ready: true },
  { key: "instagram", label: "Instagram", ready: false },
  { key: "facebook", label: "Facebook", ready: false },
  { key: "twitter", label: "Twitter", ready: false },
  { key: "linkedin", label: "LinkedIn", ready: false },
  { key: "google", label: "Google", ready: false },
];

const PIN_TYPES = [
  { key: "IMAGE", label: "Image Pin", icon: "mdi:image-outline" },
  { key: "VIDEO", label: "Video Pin", icon: "mdi:video-outline" },
  { key: "CAROUSEL", label: "Carousel", icon: "mdi:image-multiple-outline" },
];

const PRIVACY_OPTIONS = [
  { key: "PUBLIC", label: "Public", hint: "Anyone can find this board" },
  { key: "PROTECTED", label: "Protected", hint: "Hidden from search" },
  { key: "SECRET", label: "Secret", hint: "Only you can see it" },
];

const TIPS = [
  {
    title: "Vertical Wins",
    body: "A 2:3 image (1000x1500) takes up more feed space than a square and consistently earns more saves.",
  },
  {
    title: "Write For Search",
    body: "Pinterest is a search engine — put the words people would actually type into the title and description.",
  },
  {
    title: "Always Add A Link",
    body: "A pin without a destination URL can still be saved, but it can never send anyone to your site.",
  },
];

const emptyPin = {
  pin_type: "IMAGE",
  board_id: "",
  board_section_id: "",
  title: "",
  description: "",
  link: "",
  alt_text: "",
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) =>
  new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

/* ------------------------------------------------------------------------ */

function PinterestPanel() {
  const [loading, setLoading] = useState(false);
  const [loadingNote, setLoadingNote] = useState("");
  const [activeTab, setActiveTab] = useState("account");

  // account
  const [account, setAccount] = useState(null);

  // boards
  const [boards, setBoards] = useState([]);
  const [sections, setSections] = useState([]);
  const [showBoardModal, setShowBoardModal] = useState(false);
  const [boardForm, setBoardForm] = useState({
    name: "",
    description: "",
    privacy: "PUBLIC",
  });
  const [confirmDeleteBoard, setConfirmDeleteBoard] = useState(null);

  // pins
  const [pins, setPins] = useState([]);
  const [pinFilterBoard, setPinFilterBoard] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  // composer
  const [showComposer, setShowComposer] = useState(false);
  const [form, setForm] = useState(emptyPin);
  const [media, setMedia] = useState(null); // { url, preview, kind }
  const [videoMediaId, setVideoMediaId] = useState("");
  const [carousel, setCarousel] = useState([]);
  const [channel, setChannel] = useState("pinterest");

  // edit
  const [showEdit, setShowEdit] = useState(false);
  const [editPin, setEditPin] = useState(null);

  // detail
  const [showDetail, setShowDetail] = useState(false);
  const [detailPin, setDetailPin] = useState(null);
  const [pinInsights, setPinInsights] = useState(null);

  // analytics
  const [range, setRange] = useState({
    start_date: daysAgoISO(29),
    end_date: todayISO(),
  });
  const [insights, setInsights] = useState(null);
  const [topPins, setTopPins] = useState([]);
  const [needsBusiness, setNeedsBusiness] = useState(false);

  const clickRef = useRef(0);

  /* ------------------------- common caller ------------------------- */
  const call = useCallback(
    async (url, fields = {}, files = {}, silent = false) => {
      const fd = new FormData();
      Object.entries(fields).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") fd.append(k, v);
      });
      Object.entries(files).forEach(([k, f]) => f && fd.append(k, f));

      try {
        const res = await server_post_data(url, fd);
        const parts = res.message.split("~@~");
        if (parseInt(parts[0]) === 1) {
          if (!silent) handleError(parts[1]);
          return null;
        }
        const parsed = JSON.parse(parts[1]);
        if (parsed.error) {
          if (!silent) handleError(parsed.message || "Pinterest API error");
          return { _error: parsed };
        }
        return parsed;
      } catch (e) {
        if (!silent) handleError("network");
        return null;
      }
    },
    [],
  );

  /* ------------------------- loaders ------------------------- */
  const loadAccount = useCallback(async () => {
    setLoading(true);
    const d = await call(get_pinterest_account);
    if (d?.account) setAccount(d.account);
    setLoading(false);
  }, [call]);

  const loadBoards = useCallback(async () => {
    const d = await call(get_pinterest_boards, { limit: 100 });
    if (d?.boards) setBoards(d.boards);
    return d?.boards || [];
  }, [call]);

  const loadPins = useCallback(
    async (boardId = pinFilterBoard) => {
      setLoading(true);
      const d = await call(get_pinterest_pins, {
        limit: 50,
        board_id: boardId || "",
      });
      setPins(d?.pins || []);
      setLoading(false);
    },
    [call, pinFilterBoard],
  );

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setNeedsBusiness(false);

    const d = await call(get_pinterest_insights, range, {}, true);
    if (d?._error) {
      if (d._error.needsBusinessAccount) {
        setNeedsBusiness(true);
      } else {
        handleError(d._error.message);
      }
      setInsights(null);
      setTopPins([]);
      setLoading(false);
      return;
    }

    setInsights(d || null);

    const t = await call(
      get_pinterest_top_pins,
      { ...range, sort_by: "IMPRESSION" },
      {},
      true,
    );
    setTopPins(t && !t._error ? t.pins : []);

    setLoading(false);
  }, [call, range]);

  useEffect(() => {
    loadAccount();
    loadBoards();
  }, [loadAccount, loadBoards]);

  useEffect(() => {
    if (activeTab === "pins") loadPins();
    if (activeTab === "boards") loadBoards();
    if (activeTab === "insights") loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  /* ------------------------- OAuth popup flow ------------------------- */
  const exchangeCode = useCallback(
    async (code, state) => {
      setLoading(true);
      const d = await call(pinterest_oauth_callback, { code, state });
      setLoading(false);
      if (!d?.account) return;

      setLoading(true);
      const saved = await call(save_pinterest_account, {
        username: d.account.username,
        access_token: d.accessToken,
        refresh_token: d.refreshToken,
        expires_in: d.expiresIn,
      });
      setLoading(false);

      if (saved) {
        handleSuccess(`Connected as ${d.account.username}`);
        loadAccount();
        loadBoards();
      }
    },
    [call, loadAccount, loadBoards],
  );

  // Pinterest has no browser SDK — the popup posts the auth code back here.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.source !== "pinterest-oauth") return;

      if (data.error) {
        handleError(data.error);
        return;
      }
      if (data.code) exchangeCode(data.code, data.state);
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [exchangeCode]);

  const handleConnect = async () => {
    const d = await call(get_pinterest_auth_url);
    if (!d?.authUrl) return;

    const w = 620;
    const h = 740;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;

    const popup = window.open(
      d.authUrl,
      "pinterest-oauth",
      `width=${w},height=${h},left=${left},top=${top}`,
    );
    if (!popup) handleError("Please allow popups to connect Pinterest");
  };

  /* ------------------------- boards ------------------------- */
  const saveBoard = async () => {
    if (!boardForm.name.trim()) {
      handleError("Board name is required");
      return;
    }
    setLoading(true);
    const d = await call(create_pinterest_board, boardForm);
    setLoading(false);
    if (d && !d._error) {
      handleSuccess("Board created successfully");
      setShowBoardModal(false);
      setBoardForm({ name: "", description: "", privacy: "PUBLIC" });
      const list = await loadBoards();
      // Newly created board becomes the composer default — that's almost
      // always why someone creates one mid-flow.
      if (d.board?.boardId) {
        setForm((s) => ({ ...s, board_id: d.board.boardId }));
      }
      return list;
    }
  };

  const removeBoard = async () => {
    setLoading(true);
    const d = await call(delete_pinterest_board, {
      board_id: confirmDeleteBoard,
    });
    setLoading(false);
    setConfirmDeleteBoard(null);
    if (d && !d._error) {
      handleSuccess("Board deleted successfully");
      loadBoards();
    }
  };

  const loadSections = useCallback(
    async (boardId) => {
      if (!boardId) {
        setSections([]);
        return;
      }
      const d = await call(
        get_pinterest_board_sections,
        { board_id: boardId },
        {},
        true,
      );
      setSections(d && !d._error ? d.sections : []);
    },
    [call],
  );

  /* ------------------------- media ------------------------- */
  // Pinterest fetches images from a public URL, so files go to our own
  // storage first. Video is different — it uses Pinterest's own upload flow.
  const onPickImage = async (file) => {
    if (!file) return;
    setLoadingNote("Uploading image...");
    setLoading(true);
    const d = await call(upload_public_media, {}, { media_file: file });
    setLoading(false);
    setLoadingNote("");
    if (!d?.url) return;
    setMedia({ url: d.url, preview: URL.createObjectURL(file), kind: "IMAGE" });
  };

  const onPickVideo = async (file) => {
    if (!file) return;

    setLoadingNote("Uploading video to Pinterest — this can take a minute...");
    setLoading(true);
    const v = await call(upload_pinterest_video, {}, { media_file: file });
    setLoading(false);
    setLoadingNote("");

    if (!v || v._error) return;
    setVideoMediaId(v.mediaId);
    setMedia({ url: "", preview: URL.createObjectURL(file), kind: "VIDEO" });
  };

  const onPickCover = async (file) => {
    if (!file) return;
    setLoadingNote("Uploading cover image...");
    setLoading(true);
    const d = await call(upload_public_media, {}, { media_file: file });
    setLoading(false);
    setLoadingNote("");
    if (!d?.url) return;
    setMedia((s) => ({
      ...(s || {}),
      coverUrl: d.url,
      coverPreview: URL.createObjectURL(file),
    }));
  };

  const onPickCarouselItem = async (file) => {
    if (!file) return;
    if (carousel.length >= 5) {
      handleError("A carousel can hold at most 5 images");
      return;
    }
    setLoadingNote("Uploading image...");
    setLoading(true);
    const d = await call(upload_public_media, {}, { media_file: file });
    setLoading(false);
    setLoadingNote("");
    if (!d?.url) return;
    setCarousel((s) => [
      ...s,
      { url: d.url, preview: URL.createObjectURL(file) },
    ]);
  };

  /* ------------------------- composer ------------------------- */
  const resetComposer = () => {
    setForm({ ...emptyPin, board_id: boards[0]?.boardId || "" });
    setMedia(null);
    setVideoMediaId("");
    setCarousel([]);
    setSections([]);
    setChannel("pinterest");
  };

  const openComposer = async () => {
    // Boards are mandatory for pinning, so make sure they're loaded before
    // the composer can be interacted with.
    let list = boards;
    if (list.length === 0) {
      setLoading(true);
      list = await loadBoards();
      setLoading(false);
    }

    setForm({ ...emptyPin, board_id: list[0]?.boardId || "" });
    setMedia(null);
    setVideoMediaId("");
    setCarousel([]);
    setChannel("pinterest");

    if (list[0]?.boardId) loadSections(list[0].boardId);
    setShowComposer(true);
  };

  const publish = async () => {
    const t = form.pin_type;

    if (!form.board_id) {
      handleError("Please choose a board for this pin");
      return;
    }
    if (t === "CAROUSEL" && carousel.length < 2) {
      handleError("A carousel needs at least 2 images");
      return;
    }
    if (t === "IMAGE" && !media?.url) {
      handleError("Please add an image first");
      return;
    }
    if (t === "VIDEO") {
      if (!videoMediaId) {
        handleError("Please upload a video first");
        return;
      }
      if (!media?.coverUrl) {
        handleError("A video pin needs a cover image");
        return;
      }
    }
    if (form.title.length > 100) {
      handleError("Title cannot exceed 100 characters");
      return;
    }
    if (form.description.length > 500) {
      handleError("Description cannot exceed 500 characters");
      return;
    }

    if (clickRef.current === 1) return;
    clickRef.current = 1;
    setLoadingNote("Creating your pin...");
    setLoading(true);

    const payload = {
      board_id: form.board_id,
      board_section_id: form.board_section_id,
      pin_type: t,
      title: form.title,
      description: form.description,
      link: form.link,
      alt_text: form.alt_text,
    };

    if (t === "CAROUSEL") {
      payload.carousel_items = JSON.stringify(
        carousel.map((c) => ({ url: c.url })),
      );
    } else if (t === "VIDEO") {
      payload.media_id = videoMediaId;
      payload.cover_image_url = media.coverUrl;
    } else {
      payload.image_url = media.url;
    }

    const d = await call(create_pinterest_pin, payload);

    setLoading(false);
    setLoadingNote("");
    clickRef.current = 0;

    if (d && !d._error) {
      handleSuccess("Pin published successfully");
      setShowComposer(false);
      loadPins();
      loadBoards();
    }
  };

  /* ------------------------- edit / delete ------------------------- */
  const openEdit = (p) => {
    setEditPin({
      pin_id: p.pinId,
      title: p.title || "",
      description: p.description || "",
      link: p.link || "",
      alt_text: p.altText || "",
      board_id: p.boardId || "",
    });
    setShowEdit(true);
  };

  const saveEdit = async () => {
    if (editPin.title.length > 100) {
      handleError("Title cannot exceed 100 characters");
      return;
    }
    setLoading(true);
    const d = await call(update_pinterest_pin, editPin);
    setLoading(false);
    if (d && !d._error) {
      handleSuccess("Pin updated successfully");
      setShowEdit(false);
      loadPins();
    }
  };

  const removePin = async () => {
    setLoading(true);
    const d = await call(delete_pinterest_pin, { pin_id: confirmDelete });
    setLoading(false);
    setConfirmDelete(null);
    if (d && !d._error) {
      handleSuccess("Pin deleted successfully");
      setShowDetail(false);
      loadPins();
    }
  };

  /* ------------------------- detail ------------------------- */
  const openDetail = async (p) => {
    setDetailPin(p);
    setPinInsights(null);
    setShowDetail(true);

    // Analytics are a business-account feature, so a failure here is shown
    // inline rather than as an error toast on every pin open.
    const d = await call(
      get_pinterest_pin_insights,
      { pin_id: p.pinId, ...range },
      {},
      true,
    );
    if (d && !d._error) setPinInsights(d.insights);
  };

  /* ------------------------- helpers ------------------------- */
  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "-";

  const compact = (n) => {
    if (n === undefined || n === null) return 0;
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n;
  };

  const boardName = (id) =>
    boards.find((b) => b.boardId === id)?.name || "Select a board";

  const isCarousel = form.pin_type === "CAROUSEL";
  const isVideo = form.pin_type === "VIDEO";

  const previewImage = isCarousel
    ? carousel[0]?.preview
    : isVideo
      ? media?.coverPreview || media?.preview
      : media?.preview;

  return (
    <MasterLayout>
      <div className="gmb-premium fb-premium pin-premium">
        {loading && <Loader />}

        {/* ------------------------- Topbar ------------------------- */}
        <div className="gmb-topbar">
          <div className="gmb-topbar-title">
            <h5 className="gmb-display">Pinterest</h5>
            <span className="gmb-royal-badge">
              <Icon icon="mdi:pinterest" /> Social Suite
            </span>
            {account && (
              <span className="gmb-live">
                <span className="gmb-live-dot" /> Live
              </span>
            )}
          </div>
          <div className="gmb-topbar-actions">
            <button
              className="btn btn-outline-primary-600"
              onClick={handleConnect}
            >
              <Icon icon="mdi:account-switch-outline" /> Switch Account
            </button>
            <button className="btn btn-primary-600" onClick={openComposer}>
              <Icon icon="ic:baseline-plus" /> Create Pin
            </button>
          </div>
        </div>

        {/* ------------------------- Account hero ------------------------- */}
        {account && (
          <div className="fb-page-hero">
            <img
              src={account.profileImage || ""}
              alt={account.username}
              onError={(e) => (e.currentTarget.style.visibility = "hidden")}
            />
            <div>
              <h6>{account.username}</h6>
              <span>
                {account.isBusiness ? "Business account" : "Personal account"}
              </span>
            </div>
            <div className="fb-page-stats">
              <div>
                <b className="gmb-mono">{compact(account.followerCount)}</b>
                <small>Followers</small>
              </div>
              <div>
                <b className="gmb-mono">{compact(account.pinCount)}</b>
                <small>Pins</small>
              </div>
              <div>
                <b className="gmb-mono">{compact(account.boardCount)}</b>
                <small>Boards</small>
              </div>
            </div>
          </div>
        )}

        {account && !account.isBusiness && (
          <div className="pin-notice warn">
            <Icon icon="mdi:briefcase-outline" className="pin-notice-icon" />
            <span>
              This is a personal Pinterest account. Publishing works, but
              analytics need a free Business account — you can convert yours in
              Pinterest settings without losing any pins.
            </span>
          </div>
        )}

        {/* ------------------------- Tabs ------------------------- */}
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <Nav className="nav-tabs">
            <Nav.Item>
              <Nav.Link eventKey="account">
                <Icon icon="mdi:information-outline" /> Profile
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="pins">
                <Icon icon="mdi:view-grid-outline" /> Pins
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="boards">
                <Icon icon="mdi:bulletin-board" /> Boards
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="insights">
                <Icon icon="mdi:chart-line" /> Analytics
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* ------------------ PROFILE ------------------ */}
            <Tab.Pane eventKey="account">
              {!account ? (
                <div className="gmb-empty">
                  No Pinterest account is connected yet — use “Switch Account”
                  above to connect one.
                </div>
              ) : (
                <div className="gmb-card" style={{ padding: 20 }}>
                  <div className="gmb-detail-row">
                    <strong>Username</strong>
                    <span>{account.username}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Account Type</strong>
                    <span>{account.accountType || "-"}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>About</strong>
                    <span>{account.about || "-"}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Website</strong>
                    <span>
                      {account.websiteUrl ? (
                        <a
                          href={account.websiteUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {account.websiteUrl}
                        </a>
                      ) : (
                        "-"
                      )}
                    </span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Followers</strong>
                    <span>{compact(account.followerCount)}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Following</strong>
                    <span>{compact(account.followingCount)}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Monthly Views</strong>
                    <span>{compact(account.monthlyViews)}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Profile</strong>
                    <span>
                      <a
                        href={account.profileUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {account.profileUrl}
                      </a>
                    </span>
                  </div>
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ PINS ------------------ */}
            <Tab.Pane eventKey="pins">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <select
                  className="form-control"
                  style={{ maxWidth: 260 }}
                  value={pinFilterBoard}
                  onChange={(e) => {
                    setPinFilterBoard(e.target.value);
                    loadPins(e.target.value);
                  }}
                >
                  <option value="">All boards</option>
                  {boards.map((b) => (
                    <option key={b.boardId} value={b.boardId}>
                      {b.name}
                    </option>
                  ))}
                </select>
                <button
                  className="btn btn-outline-primary-600 btn-sm"
                  onClick={() => loadPins()}
                >
                  <Icon icon="mdi:refresh" /> Refresh
                </button>
              </div>

              {pins.length === 0 ? (
                <div className="gmb-empty">No pins published yet.</div>
              ) : (
                <div className="pin-masonry">
                  {pins.map((p) => (
                    <div
                      className="pin-card"
                      key={p.pinId}
                      onClick={() => openDetail(p)}
                    >
                      {p.thumbnailUrl && (
                        <img src={p.thumbnailUrl} alt={p.title} />
                      )}

                      {p.mediaType !== "image" && (
                        <span className="pin-card-type">
                          <Icon
                            icon={
                              p.mediaType === "video"
                                ? "mdi:play"
                                : "mdi:image-multiple-outline"
                            }
                          />
                        </span>
                      )}

                      <div className="pin-card-actions">
                        <button
                          className="pin-card-action"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(p);
                          }}
                        >
                          <Icon icon="lucide:edit" />
                        </button>
                        <button
                          className="pin-card-action danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDelete(p.pinId);
                          }}
                        >
                          <Icon icon="fluent:delete-24-regular" />
                        </button>
                      </div>

                      <div className="pin-card-body">
                        <b>{p.title || "(untitled pin)"}</b>
                        {p.description && <p>{p.description}</p>}
                        {p.link && (
                          <span className="pin-card-link">
                            <Icon icon="mdi:link-variant" />
                            {p.link.replace(/^https?:\/\//, "").slice(0, 32)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ BOARDS ------------------ */}
            <Tab.Pane eventKey="boards">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="gmb-tab-sub mb-0">
                  Every pin lives on a board — organise them by topic so
                  Pinterest can categorise your content.
                </div>
                <button
                  className="btn btn-primary-600"
                  onClick={() => {
                    setBoardForm({
                      name: "",
                      description: "",
                      privacy: "PUBLIC",
                    });
                    setShowBoardModal(true);
                  }}
                >
                  <Icon icon="ic:baseline-plus" /> Create Board
                </button>
              </div>

              {boards.length === 0 ? (
                <div className="gmb-empty">
                  No boards yet — create one before publishing your first pin.
                </div>
              ) : (
                <div className="row g-3">
                  {boards.map((b) => (
                    <div className="col-xl-3 col-lg-4 col-md-6" key={b.boardId}>
                      <div className="gmb-card pin-board-card">
                        {b.coverImage ? (
                          <img
                            className="pin-board-card-cover"
                            src={b.coverImage}
                            alt={b.name}
                          />
                        ) : (
                          <div className="pin-board-card-cover-empty">
                            <Icon icon="mdi:bulletin-board" />
                          </div>
                        )}

                        <div className="pin-board-card-body">
                          <h6>{b.name}</h6>
                          {b.description && <p>{b.description}</p>}

                          <div className="pin-board-meta">
                            <span>
                              <Icon icon="mdi:pin-outline" />{" "}
                              {compact(b.pinCount)}
                            </span>
                            <span>
                              <Icon icon="mdi:account-outline" />{" "}
                              {compact(b.followerCount)}
                            </span>
                            <span
                              className={`pin-privacy-chip ${b.privacy.toLowerCase()}`}
                            >
                              {b.privacy}
                            </span>
                          </div>

                          <div className="d-flex gap-2 mt-3">
                            <button
                              className="btn btn-outline-primary-600 btn-sm"
                              onClick={() => {
                                setPinFilterBoard(b.boardId);
                                setActiveTab("pins");
                                loadPins(b.boardId);
                              }}
                            >
                              <Icon icon="mdi:view-grid-outline" /> View Pins
                            </button>
                            <button
                              className="btn btn-outline-danger-600 btn-sm"
                              onClick={() => setConfirmDeleteBoard(b.boardId)}
                            >
                              <Icon icon="fluent:delete-24-regular" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ ANALYTICS ------------------ */}
            <Tab.Pane eventKey="insights">
              <div className="gmb-date-row">
                <div>
                  <label>Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={range.start_date}
                    onChange={(e) =>
                      setRange((s) => ({ ...s, start_date: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label>End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={range.end_date}
                    onChange={(e) =>
                      setRange((s) => ({ ...s, end_date: e.target.value }))
                    }
                  />
                </div>
                <button className="btn btn-primary-600" onClick={loadAnalytics}>
                  <Icon icon="mdi:magnify" /> Fetch
                </button>
              </div>

              {needsBusiness ? (
                <div className="pin-notice warn">
                  <Icon
                    icon="mdi:briefcase-outline"
                    className="pin-notice-icon"
                  />
                  <span>
                    Pinterest only provides analytics to Business accounts.
                    Converting is free and keeps all your existing pins and
                    boards — you can do it from Pinterest settings, then
                    reconnect here.
                  </span>
                </div>
              ) : !insights ? (
                <div className="gmb-empty">
                  Select a date range and click Fetch to load analytics.
                </div>
              ) : (
                <>
                  <div className="row g-3 mb-4">
                    {Object.entries(insights.insights || {}).map(([key, m]) => (
                      <div className="col-xl-3 col-lg-4 col-md-6" key={key}>
                        <div className="gmb-card gmb-metric-card">
                          <div
                            className="gmb-metric-icon"
                            style={{
                              background: "#fde2e4",
                              color: "var(--pin-red)",
                            }}
                          >
                            <Icon
                              icon={
                                {
                                  IMPRESSION: "mdi:eye-outline",
                                  SAVE: "mdi:content-save-outline",
                                  PIN_CLICK: "mdi:cursor-default-click-outline",
                                  OUTBOUND_CLICK: "mdi:open-in-new",
                                  TOTAL_COMMENTS: "mdi:comment-outline",
                                  TOTAL_REACTIONS: "mdi:heart-outline",
                                }[key] || "mdi:chart-box-outline"
                              }
                            />
                          </div>
                          <div className="gmb-metric-label">{m.title}</div>
                          <div className="gmb-metric-total gmb-mono">
                            {m.isRate
                              ? `${Number(m.total).toFixed(2)}%`
                              : compact(m.total)}
                          </div>
                          <div className="gmb-metric-range">
                            <span>{range.start_date}</span>
                            <span>{range.end_date}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {topPins.length > 0 && (
                    <div className="gmb-card" style={{ padding: 20 }}>
                      <div className="gmb-section-title mb-3">
                        Top Performing Pins
                      </div>
                      {topPins.slice(0, 10).map((p, i) => (
                        <div className="pin-top-row" key={p.pinId}>
                          <span className="pin-top-rank">{i + 1}</span>
                          <div className="pin-top-text">
                            <a
                              className="gmb-competitor-maps-link"
                              href={p.permalink}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View pin {p.pinId}
                            </a>
                          </div>
                          <div className="pin-top-stats">
                            <div>
                              <b>{compact(p.impressions)}</b>
                              <small>Impressions</small>
                            </div>
                            <div>
                              <b>{compact(p.saves)}</b>
                              <small>Saves</small>
                            </div>
                            <div>
                              <b>{compact(p.outboundClicks)}</b>
                              <small>Clicks</small>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        {/* ==================== COMPOSER MODAL ==================== */}
        <Modal
          className="gmb-modal fb-composer-modal"
          size="xl"
          centered
          show={showComposer}
          onHide={() => setShowComposer(false)}
        >
          <Modal.Body>
            <div className="fb-composer">
              {/* ---------- LEFT: profiles + board picker ---------- */}
              <div className="fb-composer-col fb-composer-rail">
                <div className="fb-composer-heading">
                  Select Social Profiles
                </div>

                {SOCIAL_PROFILES.map((sp) => {
                  const connected = sp.key === "pinterest" && !!account;
                  return (
                    <div
                      key={sp.key}
                      className={`fb-social-item ${sp.key} ${
                        connected ? "is-connected" : ""
                      }`}
                    >
                      <div className="fb-social-head">
                        <Icon icon={sp.icon} />
                        {sp.label}
                        {connected && (
                          <span className="fb-social-count">(1)</span>
                        )}
                      </div>
                      <div className="fb-social-foot">
                        <button
                          type="button"
                          disabled={!sp.ready}
                          onClick={sp.ready ? handleConnect : undefined}
                        >
                          {connected ? "Connected" : "Connect Account"}
                        </button>
                        <span className="fb-social-status">
                          <Icon
                            icon={
                              connected
                                ? "mdi:check-circle"
                                : "mdi:check-circle-outline"
                            }
                          />
                        </span>
                      </div>
                    </div>
                  );
                })}

                <div className="fb-rail-divider" />

                <div className="fb-field-title">
                  Choose Board{" "}
                  <span style={{ color: "var(--pin-red)" }}>*</span>
                </div>

                {boards.length === 0 ? (
                  <div className="gmb-char-count">
                    No boards yet — create one to start pinning.
                  </div>
                ) : (
                  <div className="pin-board-list">
                    {boards.map((b) => {
                      const active = form.board_id === b.boardId;
                      return (
                        <button
                          key={b.boardId}
                          type="button"
                          className={`pin-board-option ${active ? "active" : ""}`}
                          onClick={() => {
                            setForm((s) => ({
                              ...s,
                              board_id: b.boardId,
                              board_section_id: "",
                            }));
                            loadSections(b.boardId);
                          }}
                        >
                          {b.coverImage ? (
                            <img
                              className="pin-board-cover"
                              src={b.coverImage}
                              alt=""
                            />
                          ) : (
                            <span className="pin-board-cover-empty">
                              <Icon icon="mdi:bulletin-board" />
                            </span>
                          )}
                          <span style={{ minWidth: 0 }}>
                            <b>
                              {b.name}
                              {b.privacy !== "PUBLIC" && (
                                <Icon
                                  icon="mdi:lock-outline"
                                  className="pin-board-lock"
                                />
                              )}
                            </b>
                            <small>{b.pinCount} pins</small>
                          </span>
                          {active && (
                            <Icon
                              icon="mdi:check-circle"
                              className="pin-board-check"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                <button
                  type="button"
                  className="pin-board-new"
                  onClick={() => {
                    setBoardForm({
                      name: "",
                      description: "",
                      privacy: "PUBLIC",
                    });
                    setShowBoardModal(true);
                  }}
                >
                  <Icon icon="mdi:plus" /> Create New Board
                </button>

                {sections.length > 0 && (
                  <>
                    <div className="fb-field-title mt-3">Board Section</div>
                    <select
                      className="form-control"
                      value={form.board_section_id}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          board_section_id: e.target.value,
                        }))
                      }
                    >
                      <option value="">No section</option>
                      {sections.map((s) => (
                        <option key={s.sectionId} value={s.sectionId}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                <div className="fb-toggle-row mt-4">
                  <span>Scheduling</span>
                  <button type="button" className="fb-switch" disabled />
                </div>
                <div className="gmb-char-count">
                  Pinterest's API has no scheduling — pins publish immediately.
                </div>
              </div>

              {/* ---------- MIDDLE: editor ---------- */}
              <div className="fb-composer-col">
                <div className="fb-composer-title">Create Pin</div>
                <div className="fb-composer-sub">
                  {account
                    ? `${account.username} selected`
                    : "No account selected"}
                </div>

                <div className="fb-channel-tabs">
                  {CHANNEL_TABS.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      disabled={!c.ready}
                      className={channel === c.key ? "active" : ""}
                      onClick={() => c.ready && setChannel(c.key)}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>

                <div className="fb-account-row">
                  <Icon
                    icon="mdi:pinterest"
                    style={{ color: "var(--pin-red)" }}
                  />
                  Saving to:
                  <span className="fb-account-chip">
                    {boardName(form.board_id)}
                  </span>
                </div>

                <div className="fb-field-title">Select Pin Type</div>
                <div className="fb-pills">
                  {PIN_TYPES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      className={`fb-pill ${
                        form.pin_type === t.key ? "active" : ""
                      }`}
                      onClick={() => {
                        setForm((s) => ({ ...s, pin_type: t.key }));
                        setMedia(null);
                        setVideoMediaId("");
                        setCarousel([]);
                      }}
                    >
                      {form.pin_type === t.key ? (
                        <Icon icon="mdi:check" />
                      ) : (
                        <Icon icon={t.icon} />
                      )}
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* ---- Carousel ---- */}
                {isCarousel ? (
                  <>
                    <div className="fb-field-title">
                      Carousel Images ( 2 - 5, vertical 1000 x 1500 recommended
                      )
                    </div>
                    <div className="pin-carousel-list">
                      {carousel.map((c, i) => (
                        <div className="pin-carousel-item" key={c.url}>
                          <img src={c.preview} alt="" />
                          <button
                            type="button"
                            onClick={() =>
                              setCarousel((s) => s.filter((_, x) => x !== i))
                            }
                          >
                            <Icon icon="mdi:close" />
                          </button>
                        </div>
                      ))}
                      {carousel.length < 5 && (
                        <label className="pin-carousel-add">
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={(e) =>
                              onPickCarouselItem(e.target.files[0])
                            }
                          />
                          <Icon icon="mdi:plus" />
                        </label>
                      )}
                    </div>
                    <div className="gmb-char-count mt-2">
                      {carousel.length} of 5 images added
                    </div>
                  </>
                ) : (
                  <>
                    <div className="fb-field-title">
                      Add Pin {isVideo ? "Video" : "Image"} ( 2:3 ratio, 1000 x
                      1500 recommended )
                    </div>

                    {media?.preview ? (
                      <div className="fb-drop-preview">
                        {media.kind === "VIDEO" ? (
                          <video src={media.preview} controls />
                        ) : (
                          <img src={media.preview} alt="preview" />
                        )}
                        <button
                          type="button"
                          className="fb-drop-remove"
                          onClick={() => {
                            setMedia(null);
                            setVideoMediaId("");
                          }}
                        >
                          <Icon icon="mdi:close" />
                        </button>
                      </div>
                    ) : (
                      <label className="fb-dropzone">
                        <input
                          type="file"
                          accept={
                            isVideo
                              ? "video/mp4,video/quicktime"
                              : "image/jpeg,image/png,image/webp"
                          }
                          onChange={(e) =>
                            isVideo
                              ? onPickVideo(e.target.files[0])
                              : onPickImage(e.target.files[0])
                          }
                        />
                        <div className="fb-drop-icon">
                          <Icon
                            icon={
                              isVideo
                                ? "mdi:video-plus-outline"
                                : "mdi:image-plus-outline"
                            }
                          />
                        </div>
                        <div className="fb-drop-title">
                          Upload or Drag {isVideo ? "Video" : "Photo"}
                        </div>
                        <div className="fb-drop-hint">
                          {isVideo
                            ? "Maximum Size - 500 MB. Format MP4, MOV"
                            : "Maximum Size - 20 MB. Format JPG, PNG, WEBP"}
                        </div>
                      </label>
                    )}

                    {isVideo && videoMediaId && (
                      <>
                        <div className="fb-field-title mt-3">
                          Cover Image{" "}
                          <span style={{ color: "var(--pin-red)" }}>*</span>
                        </div>
                        {media?.coverPreview ? (
                          <div className="fb-drop-preview">
                            <img src={media.coverPreview} alt="cover" />
                            <button
                              type="button"
                              className="fb-drop-remove"
                              onClick={() =>
                                setMedia((s) => ({
                                  ...s,
                                  coverUrl: "",
                                  coverPreview: "",
                                }))
                              }
                            >
                              <Icon icon="mdi:close" />
                            </button>
                          </div>
                        ) : (
                          <label className="fb-dropzone">
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              onChange={(e) => onPickCover(e.target.files[0])}
                            />
                            <div className="fb-drop-icon">
                              <Icon icon="mdi:image-plus-outline" />
                            </div>
                            <div className="fb-drop-title">
                              Upload Cover Image
                            </div>
                            <div className="fb-drop-hint">
                              Pinterest requires a still cover for every video
                              pin
                            </div>
                          </label>
                        )}
                      </>
                    )}
                  </>
                )}

                <div className="fb-field-title mt-4">Pin Title</div>
                <input
                  className="form-control"
                  placeholder="Eg: 5 Easy Weeknight Dinners"
                  maxLength={100}
                  value={form.title}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, title: e.target.value }))
                  }
                />
                <div className="gmb-char-count">
                  {form.title.length} / 100 characters
                </div>

                <div className="fb-field-title mt-3">Description</div>
                <textarea
                  className="form-control"
                  rows={4}
                  placeholder="Describe your pin using words people would search for"
                  maxLength={500}
                  value={form.description}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, description: e.target.value }))
                  }
                />
                <div className="gmb-char-count">
                  {form.description.length} / 500 characters
                </div>

                <div className="fb-field-title mt-3">Destination Link</div>
                <input
                  className="form-control"
                  placeholder="https://example.com/recipe"
                  value={form.link}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, link: e.target.value }))
                  }
                />

                <div className="fb-field-title mt-3">Alt Text (optional)</div>
                <input
                  className="form-control"
                  placeholder="Describe the image for screen readers"
                  maxLength={500}
                  value={form.alt_text}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, alt_text: e.target.value }))
                  }
                />

                <div className="fb-submit-bar">
                  <button
                    type="button"
                    className="btn btn-outline-primary-600"
                    onClick={() => setShowComposer(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="fb-submit"
                    disabled={!account || !form.board_id}
                    onClick={publish}
                  >
                    <Icon icon="mdi:pinterest" />
                    Publish To Pinterest
                  </button>
                </div>
              </div>

              {/* ---------- RIGHT: preview + tips ---------- */}
              <div className="fb-composer-col fb-composer-aside">
                <div className="fb-preview">
                  <div className="fb-preview-head">
                    <Icon
                      icon="mdi:pinterest"
                      style={{ color: "var(--pin-red)" }}
                    />
                    Pinterest Preview
                  </div>

                  <div className="pin-preview-card">
                    {previewImage ? (
                      <img
                        className="pin-preview-media"
                        src={previewImage}
                        alt=""
                      />
                    ) : (
                      <div className="pin-preview-empty">
                        <Icon
                          icon="mdi:image-outline"
                          style={{ fontSize: 34 }}
                        />
                        No Image Added
                      </div>
                    )}

                    <div className="pin-preview-body">
                      <b>{form.title || "Your pin title"}</b>
                      <p>{form.description || "Your description here"}</p>
                      {form.link && (
                        <span className="pin-preview-dest">
                          <Icon icon="mdi:link-variant" />
                          {form.link.replace(/^https?:\/\//, "").slice(0, 34)}
                        </span>
                      )}
                    </div>

                    <div className="pin-preview-board">
                      <Icon icon="mdi:bulletin-board" />
                      {boardName(form.board_id)}
                      <span className="pin-preview-save">Save</span>
                    </div>
                  </div>
                </div>

                <div className="fb-composer-heading">Tips to Improve Reach</div>
                {TIPS.map((t) => (
                  <div className="fb-tip-card" key={t.title}>
                    <div className="fb-tip-icon">
                      <Icon icon="mdi:lightbulb-on-outline" />
                    </div>
                    <h6>{t.title}</h6>
                    <p>{t.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* ==================== PIN DETAIL ==================== */}
        <Modal
          className="gmb-modal"
          size="lg"
          centered
          show={showDetail}
          onHide={() => setShowDetail(false)}
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title>Pin Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {detailPin && (
              <div className="row g-3">
                <div className="col-md-5">
                  {detailPin.thumbnailUrl && (
                    <img
                      src={detailPin.thumbnailUrl}
                      alt=""
                      style={{
                        width: "100%",
                        borderRadius: 12,
                        border: "1px solid var(--gmb-line)",
                      }}
                    />
                  )}
                </div>
                <div className="col-md-7">
                  <div className="gmb-review-author">
                    {detailPin.title || "(untitled pin)"}
                  </div>
                  <p className="gmb-review-comment">
                    {detailPin.description || "(no description)"}
                  </p>

                  <div className="gmb-detail-row">
                    <strong>Board</strong>
                    <span>{boardName(detailPin.boardId)}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Created</strong>
                    <span>{fmtDate(detailPin.createdAt)}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Link</strong>
                    <span>
                      {detailPin.link ? (
                        <a
                          href={detailPin.link}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {detailPin.link}
                        </a>
                      ) : (
                        "-"
                      )}
                    </span>
                  </div>

                  {pinInsights ? (
                    <div className="row g-2 mt-2">
                      {Object.entries(pinInsights).map(([k, v]) => (
                        <div className="col-6" key={k}>
                          <div className="gmb-metric-label">{v.title}</div>
                          <div className="gmb-mono" style={{ fontWeight: 800 }}>
                            {compact(v.value)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="pin-notice info mt-3">
                      <Icon
                        icon="mdi:chart-box-outline"
                        className="pin-notice-icon"
                      />
                      <span>
                        Per-pin analytics need a Pinterest Business account.
                      </span>
                    </div>
                  )}

                  <div className="d-flex gap-2 mt-3">
                    <a
                      className="btn btn-outline-primary-600 btn-sm"
                      href={detailPin.permalink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Icon icon="mdi:open-in-new" /> View on Pinterest
                    </a>
                    <button
                      className="btn btn-outline-primary-600 btn-sm"
                      onClick={() => {
                        setShowDetail(false);
                        openEdit(detailPin);
                      }}
                    >
                      <Icon icon="lucide:edit" /> Edit
                    </button>
                    <button
                      className="btn btn-outline-danger-600 btn-sm"
                      onClick={() => setConfirmDelete(detailPin.pinId)}
                    >
                      <Icon icon="fluent:delete-24-regular" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
        </Modal>

        {/* ==================== EDIT PIN ==================== */}
        <Modal
          className="gmb-modal"
          centered
          show={showEdit}
          onHide={() => setShowEdit(false)}
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title>Edit Pin</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editPin && (
              <>
                <div className="pin-notice info">
                  <Icon
                    icon="mdi:information-outline"
                    className="pin-notice-icon"
                  />
                  <span>
                    Pinterest doesn't allow the image or video to be swapped
                    after publishing — only the text and board can change.
                  </span>
                </div>

                <label className="gmb-field-label">Title</label>
                <input
                  className="form-control mb-3"
                  maxLength={100}
                  value={editPin.title}
                  onChange={(e) =>
                    setEditPin((s) => ({ ...s, title: e.target.value }))
                  }
                />

                <label className="gmb-field-label">Description</label>
                <textarea
                  className="form-control mb-3"
                  rows={4}
                  maxLength={500}
                  value={editPin.description}
                  onChange={(e) =>
                    setEditPin((s) => ({ ...s, description: e.target.value }))
                  }
                />

                <label className="gmb-field-label">Destination Link</label>
                <input
                  className="form-control mb-3"
                  value={editPin.link}
                  onChange={(e) =>
                    setEditPin((s) => ({ ...s, link: e.target.value }))
                  }
                />

                <label className="gmb-field-label">Move To Board</label>
                <select
                  className="form-control"
                  value={editPin.board_id}
                  onChange={(e) =>
                    setEditPin((s) => ({ ...s, board_id: e.target.value }))
                  }
                >
                  {boards.map((b) => (
                    <option key={b.boardId} value={b.boardId}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-outline-primary-600"
              onClick={() => setShowEdit(false)}
            >
              Cancel
            </button>
            <button className="btn btn-primary-600" onClick={saveEdit}>
              Save Changes
            </button>
          </Modal.Footer>
        </Modal>

        {/* ==================== CREATE BOARD ==================== */}
        <Modal
          className="gmb-modal"
          centered
          show={showBoardModal}
          onHide={() => setShowBoardModal(false)}
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title>Create Board</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <label className="gmb-field-label">Board Name</label>
            <input
              className="form-control mb-3"
              placeholder="Eg: Weeknight Recipes"
              maxLength={180}
              value={boardForm.name}
              onChange={(e) =>
                setBoardForm((s) => ({ ...s, name: e.target.value }))
              }
            />

            <label className="gmb-field-label">Description (optional)</label>
            <textarea
              className="form-control mb-3"
              rows={3}
              maxLength={500}
              placeholder="What is this board about?"
              value={boardForm.description}
              onChange={(e) =>
                setBoardForm((s) => ({ ...s, description: e.target.value }))
              }
            />

            <label className="gmb-field-label">Privacy</label>
            <select
              className="form-control"
              value={boardForm.privacy}
              onChange={(e) =>
                setBoardForm((s) => ({ ...s, privacy: e.target.value }))
              }
            >
              {PRIVACY_OPTIONS.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label} — {p.hint}
                </option>
              ))}
            </select>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-outline-primary-600"
              onClick={() => setShowBoardModal(false)}
            >
              Cancel
            </button>
            <button className="btn btn-primary-600" onClick={saveBoard}>
              Create Board
            </button>
          </Modal.Footer>
        </Modal>

        {/* ==================== DELETE PIN CONFIRM ==================== */}
        <Modal
          className="gmb-modal"
          size="sm"
          centered
          show={!!confirmDelete}
          onHide={() => setConfirmDelete(null)}
        >
          <Modal.Body className="text-center">
            <Icon
              icon="fluent:delete-24-regular"
              style={{ fontSize: 34, color: "var(--danger-main)" }}
            />
            <p className="mt-2 mb-0">
              Please confirm, do you want to delete this pin?
            </p>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-outline-primary-600"
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </button>
            <button className="btn btn-danger-600" onClick={removePin}>
              Confirm
            </button>
          </Modal.Footer>
        </Modal>

        {/* ==================== DELETE BOARD CONFIRM ==================== */}
        <Modal
          className="gmb-modal"
          size="sm"
          centered
          show={!!confirmDeleteBoard}
          onHide={() => setConfirmDeleteBoard(null)}
        >
          <Modal.Body className="text-center">
            <Icon
              icon="fluent:delete-24-regular"
              style={{ fontSize: 34, color: "var(--danger-main)" }}
            />
            <p className="mt-2 mb-0">
              Deleting this board will also delete every pin on it. Continue?
            </p>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-outline-primary-600"
              onClick={() => setConfirmDeleteBoard(null)}
            >
              Cancel
            </button>
            <button className="btn btn-danger-600" onClick={removeBoard}>
              Confirm
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    </MasterLayout>
  );
}

export default PinterestPanel;
